import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import axios from "axios";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from "date-fns";
import { tr } from "date-fns/locale";
import { 
  Trophy, 
  Flame, 
  TrendingUp, 
  Target, 
  Users, 
  Phone, 
  DollarSign,
  Star,
  Zap,
  Settings,
  Crown,
  Award,
  Volume2,
  VolumeX,
  Banknote,
  Calendar,
  ChevronDown,
  Wallet,
  AlertTriangle,
  Clock,
  LogOut
} from "lucide-react";
import { Progress } from "../components/ui/progress";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Create axios instance with auth
const authAxios = axios.create();
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Sound URLs
const SOUNDS = {
  applause: `${BACKEND_URL}/api/static/sounds/606fd187_driken5482-applause-cheer-236786.mp3`,
  firework: `${BACKEND_URL}/api/static/sounds/16dc8274_driken5482-crowd-cheer-in-school-auditorium-236699.mp3`
};

// Global Audio Context for reliable sound
let globalAudioContext = null;

const getAudioContext = () => {
  if (!globalAudioContext) {
    globalAudioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (globalAudioContext.state === 'suspended') {
    globalAudioContext.resume();
  }
  return globalAudioContext;
};

// Countdown beep sound
const createCountdownBeep = (countNumber) => {
  try {
    const audioCtx = getAudioContext();
    const baseFreq = 400 + ((10 - countNumber) * 50);
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(baseFreq, audioCtx.currentTime);
    oscillator.frequency.linearRampToValueAtTime(baseFreq + 200, audioCtx.currentTime + 0.1);
    gainNode.gain.setValueAtTime(0.5, audioCtx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.2);
    oscillator.start(audioCtx.currentTime);
    oscillator.stop(audioCtx.currentTime + 0.2);
  } catch (e) {
    console.log('Beep error:', e);
  }
};

const createDramaticBeep = () => {
  try {
    const audioCtx = getAudioContext();
    [600, 800, 1000, 1200].forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.type = i % 2 === 0 ? 'square' : 'sawtooth';
      osc.frequency.setValueAtTime(freq, audioCtx.currentTime);
      gain.gain.setValueAtTime(0.15, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.4);
      osc.start(audioCtx.currentTime + i * 0.02);
      osc.stop(audioCtx.currentTime + 0.4);
    });
  } catch (e) {
    console.log('Dramatic beep error:', e);
  }
};

// Countdown Celebration Component
const CountdownCelebration = ({ sale, onComplete }) => {
  const [countdown, setCountdown] = useState(10);
  const [phase, setPhase] = useState('countdown'); // countdown | celebration
  const timerRef = useRef(null);
  const audioRef = useRef({ applause: null, firework: null });

  // Start countdown on mount
  useEffect(() => {
    // Preload audio
    audioRef.current.applause = new Audio(SOUNDS.applause);
    audioRef.current.firework = new Audio(SOUNDS.firework);
    
    // Initial beep
    try { createCountdownBeep(10); } catch(e) {}

    // Start interval timer
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        const next = prev - 1;
        
        // Play sounds
        try {
          if (next <= 3 && next > 0) {
            createDramaticBeep();
          } else if (next > 0) {
            createCountdownBeep(next);
          }
        } catch(e) {}
        
        // When countdown reaches 0
        if (next <= 0) {
          clearInterval(timerRef.current);
          setPhase('celebration');
        }
        
        return Math.max(0, next);
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Celebration phase
  useEffect(() => {
    if (phase === 'celebration') {
      // Play sounds
      try {
        if (audioRef.current.firework) audioRef.current.firework.play().catch(() => {});
        setTimeout(() => {
          if (audioRef.current.applause) audioRef.current.applause.play().catch(() => {});
        }, 300);
      } catch(e) {}

      // Fire confetti
      const colors = ['#10b981', '#fbbf24', '#3b82f6', '#f43f5e', '#8b5cf6', '#ec4899'];
      const end = Date.now() + 5000;
      const frame = () => {
        confetti({ particleCount: 6, angle: 60, spread: 80, origin: { x: 0, y: 0.6 }, colors });
        confetti({ particleCount: 6, angle: 120, spread: 80, origin: { x: 1, y: 0.6 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();

      // Complete after 6 seconds
      setTimeout(() => onComplete(), 6000);
    }
  }, [phase, onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95"
    >
      <AnimatePresence mode="wait">
        {phase === 'countdown' ? (
          <motion.div
            key="countdown"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 2, opacity: 0 }}
            className="text-center"
          >
            <motion.div
              key={countdown}
              initial={{ scale: 2, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="font-heading font-black text-[200px] leading-none"
              style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 100px rgba(251,191,36,0.5)'
              }}
            >
              {countdown}
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-2xl text-amber-400/80 mt-4 font-medium"
            >
              YENİ SATIŞ GELİYOR!
            </motion.p>
          </motion.div>
        ) : (
          <motion.div
            key="name"
            initial={{ scale: 0, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", damping: 10, stiffness: 100 }}
            className="text-center"
          >
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-3xl text-emerald-400 mb-4 font-medium"
            >
              🎉 TEBRİKLER! 🎉
            </motion.div>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
              className="font-heading font-black text-7xl md:text-8xl mb-6"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 80px rgba(16,185,129,0.5)'
              }}
            >
              {sale.agent_name}
            </motion.div>
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-4xl text-white font-bold"
            >
              SATIŞ YAPTI!
            </motion.div>
            {sale.amount && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-6 text-5xl font-mono font-bold text-amber-400"
              >
                ₺{sale.amount.toLocaleString()}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// 15 Second Fireworks
const fireFireworks = () => {
  const duration = 3000; // Sadece 3 saniye
  const animationEnd = Date.now() + duration;
  const colors = ['#10b981', '#fbbf24', '#3b82f6', '#f43f5e', '#8b5cf6', '#ec4899'];

  const interval = setInterval(() => {
    const timeLeft = animationEnd - Date.now();
    if (timeLeft <= 0) {
      clearInterval(interval);
      return;
    }

    const particleCount = 30 * (timeLeft / duration);
    confetti({
      particleCount: Math.floor(particleCount),
      spread: 70,
      origin: { x: Math.random(), y: Math.random() * 0.5 },
      colors: colors,
      zIndex: 9999
    });
  }, 250);
};

// Animated Counter
const AnimatedCounter = ({ value, prefix = "", suffix = "", duration = 1 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = typeof value === 'number' ? value : parseFloat(value) || 0;
    const increment = end / (duration * 60);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) { setDisplayValue(end); clearInterval(timer); }
      else { setDisplayValue(Math.floor(start)); }
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span>{prefix}{displayValue.toLocaleString('tr-TR')}{suffix}</span>;
};

// Badge Icon
const BadgeIcon = ({ badge, size = 16 }) => {
  const badges = {
    daily_star: { icon: Star, color: "text-blue-400" },
    sales_champion: { icon: Trophy, color: "text-amber-400" },
    call_master: { icon: Phone, color: "text-emerald-400" },
    top_performer: { icon: Zap, color: "text-purple-400" },
    streak_fire: { icon: Flame, color: "text-rose-400" },
  };
  const config = badges[badge] || { icon: Award, color: "text-zinc-400" };
  const Icon = config.icon;
  return <Icon size={size} className={config.color} />;
};

// Leaderboard Item
const LeaderboardItem = ({ entry, index, type = "sales" }) => {
  const getRankStyle = (rank) => {
    if (rank === 1) return "bg-gradient-to-r from-amber-500/20 to-transparent";
    if (rank === 2) return "bg-gradient-to-r from-slate-400/10 to-transparent";
    if (rank === 3) return "bg-gradient-to-r from-orange-500/10 to-transparent";
    return "bg-zinc-900/30";
  };

  const getRankNumber = (rank) => {
    const styles = {
      1: "bg-gradient-to-br from-amber-400 to-yellow-600 text-black shadow-[0_0_15px_rgba(251,191,36,0.5)]",
      2: "bg-gradient-to-br from-slate-300 to-slate-500 text-black shadow-[0_0_10px_rgba(148,163,184,0.4)]",
      3: "bg-gradient-to-br from-orange-400 to-amber-600 text-black shadow-[0_0_10px_rgba(251,146,60,0.4)]",
      4: "bg-zinc-700 text-zinc-300",
      5: "bg-zinc-700 text-zinc-300",
      6: "bg-zinc-800 text-zinc-400"
    };
    return (
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${styles[rank] || styles[6]}`}>
        {rank}
      </div>
    );
  };

  const getValue = () => {
    if (type === "revenue" || type === "daily_revenue") {
      return <span className="text-amber-400">₺{entry.total_revenue.toLocaleString()}</span>;
    }
    return <span className="text-emerald-400">{entry.total_sales}</span>;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`flex items-center gap-3 p-2.5 rounded-xl ${getRankStyle(entry.rank)} hover:bg-white/5 transition-all`}
      data-testid={`leaderboard-item-${type}-${entry.rank}`}
    >
      {getRankNumber(entry.rank)}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-white text-base truncate">{entry.name.toUpperCase()}</h3>
      </div>
      <div className="text-right">
        <div className="font-mono font-bold text-xl">{getValue()}</div>
      </div>
    </motion.div>
  );
};

// Leader Card
const LeaderCard = ({ leader, type, icon: Icon, color, label }) => {
  if (!leader) return null;
  return (
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className={`p-4 rounded-2xl bg-gradient-to-br ${color} border border-white/10`}>
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-5 h-5" />
        <span className="text-sm font-medium text-zinc-300">{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full flex items-center justify-center font-heading font-bold text-xl shadow-lg" style={{ backgroundColor: leader.avatar_color }}>
          {leader.name.charAt(0)}
        </div>
        <div>
          <h3 className="font-heading font-bold text-xl text-white">{leader.name}</h3>
          <p className="font-mono text-lg">
            {type === "revenue" ? <span className="text-amber-400">₺{leader.total_revenue.toLocaleString()}</span> : <span className="text-emerald-400">{leader.total_sales} satış</span>}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Metric Card
const MetricCard = ({ icon: Icon, label, value, color = "emerald", prefix = "", suffix = "" }) => {
  const colorConfig = {
    emerald: { text: "text-emerald-400", bg: "bg-emerald-400/10", border: "border-emerald-500/20", glow: "shadow-[0_0_30px_rgba(16,185,129,0.15)]" },
    blue: { text: "text-blue-400", bg: "bg-blue-400/10", border: "border-blue-500/20", glow: "shadow-[0_0_30px_rgba(59,130,246,0.15)]" },
    amber: { text: "text-amber-400", bg: "bg-amber-400/10", border: "border-amber-500/20", glow: "shadow-[0_0_30px_rgba(251,191,36,0.15)]" },
    rose: { text: "text-rose-400", bg: "bg-rose-400/10", border: "border-rose-500/20", glow: "shadow-[0_0_30px_rgba(244,63,94,0.15)]" },
    purple: { text: "text-purple-400", bg: "bg-purple-400/10", border: "border-purple-500/20", glow: "shadow-[0_0_30px_rgba(168,85,247,0.15)]" },
  };
  const cfg = colorConfig[color];
  return (
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} whileHover={{ scale: 1.02 }}
      className={`relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-zinc-900/80 to-zinc-950/80 border ${cfg.border} ${cfg.glow} backdrop-blur-sm`}>
      <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full ${cfg.bg} blur-2xl`} />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <div className={`p-2 rounded-lg ${cfg.bg}`}><Icon size={18} className={cfg.text} /></div>
          <span className="text-zinc-400 text-sm">{label}</span>
        </div>
        <div className={`font-heading font-bold text-3xl ${cfg.text}`}>
          <AnimatedCounter value={value} prefix={prefix} suffix={suffix} />
        </div>
      </div>
    </motion.div>
  );
};

// Date Filter Component
const DateFilter = ({ dateRange, setDateRange, onApply }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const presets = [
    { label: "Bugün", getValue: () => { const today = new Date(); return { from: today, to: today }; }},
    { label: "Bu Hafta", getValue: () => { const now = new Date(); return { from: startOfWeek(now, { locale: tr }), to: endOfWeek(now, { locale: tr }) }; }},
    { label: "Bu Ay", getValue: () => { const now = new Date(); return { from: startOfMonth(now), to: endOfMonth(now) }; }},
    { label: "Şubat 2026", getValue: () => ({ from: new Date(2026, 1, 1), to: new Date(2026, 1, 28) })},
    { label: "Tüm Zamanlar", getValue: () => ({ from: null, to: null })},
  ];

  const getDisplayText = () => {
    if (!dateRange.from && !dateRange.to) return "Tüm Zamanlar";
    if (dateRange.from && dateRange.to) {
      if (dateRange.from.toDateString() === dateRange.to.toDateString()) {
        return format(dateRange.from, "d MMMM yyyy", { locale: tr });
      }
      return `${format(dateRange.from, "d MMM", { locale: tr })} - ${format(dateRange.to, "d MMM yyyy", { locale: tr })}`;
    }
    return "Tarih Seç";
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2 border-zinc-700 hover:border-emerald-500/50 bg-zinc-900/50" data-testid="date-filter-btn">
          <Calendar size={16} className="text-emerald-400" />
          <span>{getDisplayText()}</span>
          <ChevronDown size={14} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700" align="end">
        <div className="p-3 border-b border-zinc-800">
          <div className="grid grid-cols-2 gap-2">
            {presets.map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="justify-start text-sm hover:bg-zinc-800"
                onClick={() => {
                  const range = preset.getValue();
                  setDateRange(range);
                  onApply(range);
                  setIsOpen(false);
                }}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="p-3">
          <CalendarPicker
            mode="range"
            selected={{ from: dateRange.from, to: dateRange.to }}
            onSelect={(range) => {
              if (range) {
                setDateRange({ from: range.from, to: range.to || range.from });
                if (range.to) {
                  onApply({ from: range.from, to: range.to });
                  setIsOpen(false);
                }
              }
            }}
            locale={tr}
            className="rounded-md"
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

// No Sales Today List - Auto scrolling
const NoSalesTodayList = ({ agents }) => {
  const getRankNumber = (index) => {
    const styles = {
      0: "bg-gradient-to-br from-red-400 to-rose-600 text-white shadow-[0_0_10px_rgba(244,63,94,0.4)]",
      1: "bg-gradient-to-br from-red-500/80 to-rose-600/80 text-white",
      2: "bg-gradient-to-br from-red-600/60 to-rose-700/60 text-white",
    };
    return (
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${styles[index % 3] || "bg-zinc-700 text-zinc-300"}`}>
        {index + 1}
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl p-3 bg-gradient-to-br from-zinc-900/60 to-zinc-950/60 border border-red-900/30 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={18} className="text-red-400" />
          <h3 className="font-heading font-bold text-base text-red-400">Bugün Satış Yok</h3>
        </div>
        <span className="text-xs text-red-400/60">{agents.length} kişi</span>
      </div>
      <div className="relative h-[280px] overflow-hidden">
        {agents.length > 0 ? (
          <motion.div
            animate={{
              y: agents.length > 6 ? [0, -(agents.length * 52 - 280)] : 0
            }}
            transition={{
              y: {
                duration: agents.length * 2,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "linear"
              }
            }}
            className="space-y-1.5"
          >
            {agents.map((agent, index) => (
              <motion.div key={agent.agent_id}
                className="flex items-center gap-3 p-2.5 rounded-xl bg-gradient-to-r from-red-500/10 to-transparent hover:bg-white/5 transition-all">
                {getRankNumber(index)}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white text-base truncate">{agent.name.toUpperCase()}</h3>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-4 text-emerald-400 text-sm">Herkes satış yaptı! 🎉</div>
        )}
      </div>
    </motion.div>
  );
};

// Days Without Sales List
const DaysWithoutSalesList = ({ agents }) => {
  const formatDays = (days) => {
    if (days >= 999) return "Hiç satış yok";
    if (days === 0) return "Bugün";
    if (days === 1) return "1 gündür";
    return `${days} gündür`;
  };

  const getRankNumber = (index) => {
    const styles = {
      0: "bg-gradient-to-br from-orange-400 to-amber-600 text-black shadow-[0_0_10px_rgba(251,146,60,0.4)]",
      1: "bg-gradient-to-br from-orange-500/80 to-amber-600/80 text-black",
      2: "bg-gradient-to-br from-orange-600/60 to-amber-700/60 text-white",
    };
    return (
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${styles[index] || "bg-zinc-700 text-zinc-300"}`}>
        {index + 1}
      </div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl p-3 bg-gradient-to-br from-zinc-900/60 to-zinc-950/60 border border-orange-900/30 h-full">
      <div className="flex items-center gap-2 mb-3">
        <Clock size={18} className="text-orange-400" />
        <h3 className="font-heading font-bold text-base text-orange-400">Satış Bekleyenler</h3>
      </div>
      <div className="space-y-1.5">
        <AnimatePresence mode="popLayout">
          {agents.slice(0, 6).map((agent, index) => (
            <motion.div key={agent.agent_id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
              transition={{ delay: index * 0.05 }}
              className="flex items-center gap-3 p-2.5 rounded-xl bg-gradient-to-r from-orange-500/10 to-transparent hover:bg-white/5 transition-all">
              {getRankNumber(index)}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white text-base truncate">{agent.name.toUpperCase()}</h3>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold text-base text-orange-400">{formatDays(agent.days_without_sale)}</div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {agents.length === 0 && <div className="text-center py-4 text-emerald-400 text-sm">Herkes aktif! 🚀</div>}
      </div>
    </motion.div>
  );
};

// Motivational Slogans Component
const MotivationalSlogans = ({ slogans }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    if (slogans.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slogans.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [slogans]);

  if (slogans.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      transition={{ delay: 0.2 }}
      className="relative overflow-hidden rounded-2xl p-3 bg-gradient-to-br from-fuchsia-500/20 via-pink-500/10 to-rose-900/20 border border-fuchsia-400/30 shadow-[0_0_40px_rgba(236,72,153,0.25)]"
    >
      <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-fuchsia-400/20 blur-2xl animate-pulse" />
      <div className="absolute -left-4 -bottom-4 w-24 h-24 rounded-full bg-pink-400/15 blur-xl" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-1">
          <div className="p-1 rounded-lg bg-fuchsia-400/20">
            <Flame size={16} className="text-fuchsia-300" />
          </div>
          <span className="text-fuchsia-200/70 text-sm font-medium">Motivasyon</span>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="font-heading font-bold text-xl text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-300 via-pink-300 to-rose-300 min-h-[28px] flex items-center"
          >
            {slogans[currentIndex]}
          </motion.div>
        </AnimatePresence>
        <div className="flex gap-1 mt-1">
          {slogans.map((_, idx) => (
            <div
              key={idx}
              className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentIndex ? 'bg-fuchsia-400 w-3' : 'bg-fuchsia-400/30'}`}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// Celebration Modal
const CelebrationModal = ({ celebration, onClose }) => {
  const [countdown, setCountdown] = useState(15);
  useEffect(() => {
    fireFireworks();
    const countdownTimer = setInterval(() => { setCountdown(prev => prev <= 1 ? 0 : prev - 1); }, 1000);
    const timer = setTimeout(onClose, 15000);
    return () => { clearTimeout(timer); clearInterval(countdownTimer); };
  }, [onClose]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/85 backdrop-blur-sm z-50" onClick={onClose}>
      <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 20 }}
        className="relative p-12 rounded-3xl bg-gradient-to-br from-zinc-900 to-zinc-950 border-2 border-amber-500/50 shadow-[0_0_100px_rgba(251,191,36,0.3)]"
        onClick={(e) => e.stopPropagation()}>
        <motion.div animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }} transition={{ duration: 2, repeat: Infinity }}
          className="absolute inset-0 border-4 border-amber-400/30 rounded-3xl" />
        <div className="text-center relative z-10">
          <motion.div animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.1, 1] }} transition={{ duration: 0.5, repeat: 5 }}>
            <Trophy className="w-24 h-24 text-amber-400 mx-auto mb-6" />
          </motion.div>
          <motion.h2 animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 0.5, repeat: Infinity }}
            className="font-heading text-5xl font-bold text-white mb-4">🎉 YENİ SATIŞ! 🎉</motion.h2>
          <p className="text-3xl text-emerald-400 font-bold mb-6">{celebration.agent_name}</p>
          {celebration.amount && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: "spring" }}
              className="text-6xl font-heading font-bold text-amber-400 mb-6">₺{celebration.amount.toLocaleString()}</motion.div>
          )}
          <div className="flex justify-center gap-4 text-4xl mb-6">
            {['🚀', '⭐', '🏆', '💰', '🔥', '🎊', '✨'].map((emoji, i) => (
              <motion.span key={i} animate={{ y: [0, -15, 0] }} transition={{ delay: i * 0.1, duration: 0.6, repeat: Infinity }}>{emoji}</motion.span>
            ))}
          </div>
          <div className="text-zinc-500 text-sm">{countdown} saniye</div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Goal Achievement Celebration Modal
const GoalCelebrationModal = ({ goalData, onClose }) => {
  const [countdown, setCountdown] = useState(10);
  const timerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const onCloseRef = useRef(onClose);

  // Update ref when onClose changes
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    // Fire confetti once
    fireFireworks();
    
    // Countdown timer
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(timerRef.current);
          // Auto close
          setTimeout(() => {
            onCloseRef.current();
          }, 500);
          return 0;
        }
        return next;
      });
    }, 1000);
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []); // Empty deps - run once on mount

  const isDaily = goalData.type === 'daily';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center bg-black/90 backdrop-blur-md z-50" onClick={onClose}>
      <motion.div initial={{ scale: 0, rotate: -360 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0 }}
        transition={{ type: "spring", stiffness: 150, damping: 15 }}
        className={`relative p-16 rounded-3xl bg-gradient-to-br from-zinc-900 to-zinc-950 border-4 ${isDaily ? 'border-emerald-500/50 shadow-[0_0_150px_rgba(16,185,129,0.4)]' : 'border-amber-500/50 shadow-[0_0_150px_rgba(251,191,36,0.4)]'}`}
        onClick={(e) => e.stopPropagation()}>
        <motion.div animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0, 0.3] }} transition={{ duration: 1.5, repeat: Infinity }}
          className={`absolute inset-0 border-4 ${isDaily ? 'border-emerald-400/40' : 'border-amber-400/40'} rounded-3xl`} />
        <div className="text-center relative z-10">
          <motion.div animate={{ rotate: [0, 360], scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }}>
            {isDaily ? (
              <Target className="w-32 h-32 text-emerald-400 mx-auto mb-6" />
            ) : (
              <Trophy className="w-32 h-32 text-amber-400 mx-auto mb-6" />
            )}
          </motion.div>
          <motion.h2 animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 0.3, repeat: Infinity }}
            className={`font-heading text-6xl font-bold mb-4 ${isDaily ? 'text-emerald-400' : 'text-amber-400'}`}>
            🎊 HEDEF TUTTU! 🎊
          </motion.h2>
          <p className="text-4xl text-white font-bold mb-6">
            {isDaily ? 'GÜNLÜK HEDEF' : 'AYLIK HEDEF'} TAMAMLANDI!
          </p>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: "spring" }}
            className={`text-5xl font-heading font-bold mb-6 ${isDaily ? 'text-emerald-400' : 'text-amber-400'}`}>
            ₺{(goalData.revenue || 0).toLocaleString()} / ₺{(goalData.target || 0).toLocaleString()}
          </motion.div>
          <p className="text-2xl text-zinc-400 mb-6">%100 Başarı!</p>
          <div className="flex justify-center gap-6 text-5xl mb-6">
            {['🎯', '🏆', '🥇', '🎉', '🚀', '💪', '⭐', '🔥'].map((emoji, i) => (
              <motion.span key={i} animate={{ y: [0, -20, 0], rotate: [0, 10, -10, 0] }} 
                transition={{ delay: i * 0.1, duration: 0.8, repeat: Infinity }}>{emoji}</motion.span>
            ))}
          </div>
          <div className="text-zinc-500 text-sm mb-4">{countdown} saniye sonra kapanacak</div>
          <Button onClick={onClose} className="bg-zinc-700 hover:bg-zinc-600 text-white px-8 py-2">
            KAPAT
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Dashboard
export default function Dashboard({ onLogout }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [revenueLeaderboard, setRevenueLeaderboard] = useState([]);
  const [dailySalesLeaderboard, setDailySalesLeaderboard] = useState([]); // Always today's sales
  const [stats, setStats] = useState(null);
  const [goal, setGoal] = useState(null);
  const [celebrations, setCelebrations] = useState([]);
  const [activeCelebration, setActiveCelebration] = useState(null);
  const [lastCelebrationId, setLastCelebrationId] = useState(() => {
    // localStorage'dan son celebration ID'yi al
    return localStorage.getItem('lastCelebrationId') || null;
  });
  const lastCelebrationIdRef = useRef(localStorage.getItem('lastCelebrationId') || null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  // Default to "Bu Ay" (This Month)
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return { from: startOfMonth(now), to: endOfMonth(now) };
  });
  const [collectedTotal, setCollectedTotal] = useState(0);
  const [goalCelebration, setGoalCelebration] = useState(null);
  const [lastDailyGoalMet, setLastDailyGoalMet] = useState(() => {
    return sessionStorage.getItem('dailyGoalCelebrationShown') === 'true';
  });
  const [lastMonthlyGoalMet, setLastMonthlyGoalMet] = useState(() => {
    return sessionStorage.getItem('monthlyGoalCelebrationShown') === 'true';
  });
  const [callsLeaderboard, setCallsLeaderboard] = useState([]);
  const [dailyCallsLeaderboard, setDailyCallsLeaderboard] = useState([]);
  const [slogans, setSlogans] = useState([]);
  const [countdownCelebration, setCountdownCelebration] = useState(null);
  const [noSalesToday, setNoSalesToday] = useState([]);
  const [daysWithoutSales, setDaysWithoutSales] = useState([]);
  const [topCaller, setTopCaller] = useState(null);
  const [lowestCaller, setLowestCaller] = useState(null);
  const [starOfDay, setStarOfDay] = useState(null);
  const [callTargets, setCallTargets] = useState([]);
  const [dailyCallTarget, setDailyCallTarget] = useState(50);
  const [newDataCount, setNewDataCount] = useState(0);

  // Clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Audio init
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = 0.8;
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, []);

  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await authAxios.get(`${API}/settings`);
        setSettings(res.data);
        if (res.data.slogans && res.data.slogans.length > 0) {
          setSlogans(res.data.slogans);
        }
        // Always fetch from slogans API for the full list
        try {
          const sloganRes = await authAxios.get(`${API}/slogans`);
          if (sloganRes.data && sloganRes.data.length > 0) {
            setSlogans(sloganRes.data.map(s => s.text || s));
          }
        } catch (e) {}
        if (slogans.length === 0) {
          setSlogans([
            "Hadi başarabilirsin!",
            "Satış geldi!",
            "Yapıyoruz!",
            "Şimdi tam zamanı!",
            "Sen de yapabilirsin!",
            "Bugün senin günün!"
          ]);
        }
        if (audioRef.current && res.data.celebration_music_url) {
          const url = res.data.celebration_music_url;
          audioRef.current.src = url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
        }
      } catch (error) { console.error("Settings fetch error:", error); }
    };
    fetchSettings();
  }, []);

  const playCelebrationSound = useCallback(() => {
    if (audioRef.current && settings?.celebration_sound_enabled && !isMuted) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log("Audio play failed:", e));
    }
  }, [settings, isMuted]);

  const fetchData = useCallback(async (range = dateRange) => {
    try {
      const params = new URLSearchParams();
      if (range.from) params.append('date_from', format(range.from, 'yyyy-MM-dd'));
      if (range.to) params.append('date_to', format(range.to, 'yyyy-MM-dd'));
      const queryString = params.toString() ? `?${params.toString()}` : '';

      // Today's date for daily leaderboard (always today, not filtered)
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayQuery = `&date_from=${today}&date_to=${today}`;

      const [salesLeaderRes, revenueLeaderRes, dailySalesRes, statsRes, goalRes, celebrationsRes, noSalesRes, daysWithoutRes, callsLeaderRes, dailyCallsRes] = await Promise.all([
        authAxios.get(`${API}/leaderboard?sort_by=total_sales${queryString ? '&' + params.toString() : ''}`),
        authAxios.get(`${API}/leaderboard?sort_by=total_revenue${queryString ? '&' + params.toString() : ''}`),
        authAxios.get(`${API}/leaderboard?sort_by=total_revenue${todayQuery}`), // Always today
        authAxios.get(`${API}/stats${queryString}`),
        authAxios.get(`${API}/goals/today`),
        authAxios.get(`${API}/celebrations?limit=5`),
        authAxios.get(`${API}/agents/no-sales-today`),
        authAxios.get(`${API}/agents/days-without-sales`),
        authAxios.get(`${API}/leaderboard?sort_by=total_calls${queryString ? '&' + params.toString() : ''}`),
        authAxios.get(`${API}/daily-calls-leaderboard`)
      ]);

      // Motivasyon endpoint'leri - hata tolere edilir
      let starRes = null, callTargetsRes = null, newDataRes = null;
      try { starRes = await authAxios.get(`${API}/star-of-the-day`); } catch(e) { console.log('Star API error:', e); }
      try { callTargetsRes = await authAxios.get(`${API}/daily-call-targets`); } catch(e) { console.log('Call targets API error:', e); }
      try { newDataRes = await authAxios.get(`${API}/crm/new-data-count`); } catch(e) { console.log('New data API error:', e); }

      setLeaderboard(salesLeaderRes.data);
      setRevenueLeaderboard(revenueLeaderRes.data);
      setDailySalesLeaderboard(dailySalesRes.data.filter(a => a.total_revenue > 0)); // Only agents with sales today
      setNoSalesToday(noSalesRes.data);
      setDaysWithoutSales(daysWithoutRes.data);
      setCallsLeaderboard(callsLeaderRes.data);
      setDailyCallsLeaderboard(dailyCallsRes.data);
      if (starRes) setStarOfDay(starRes.data);
      if (callTargetsRes) {
        setCallTargets(callTargetsRes.data.targets || []);
        setDailyCallTarget(callTargetsRes.data.daily_call_target || 50);
      }
      if (newDataRes) setNewDataCount(newDataRes.data.new_today || 0);
      setStats(statsRes.data);
      setGoal(goalRes.data);
      setCelebrations(celebrationsRes.data);

      // Set top and lowest callers
      if (callsLeaderRes.data.length > 0) {
        setTopCaller(callsLeaderRes.data[0]);
        // Find agent with lowest calls (but > 0)
        const withCalls = callsLeaderRes.data.filter(a => a.total_calls > 0);
        if (withCalls.length > 1) {
          setLowestCaller(withCalls[withCalls.length - 1]);
        }
      }

      // Check if daily goal is met (Revenue based)
      const dailyGoalTarget = goalRes.data?.revenue_target || 0;
      const dailyRevenue = statsRes.data?.total_revenue_today || 0;
      const dailyGoalMet = dailyGoalTarget > 0 && dailyRevenue >= dailyGoalTarget;
      
      if (dailyGoalMet && !lastDailyGoalMet && sessionStorage.getItem('dailyGoalCelebrationShown') !== 'true') {
        setGoalCelebration({ type: 'daily', revenue: dailyRevenue, target: dailyGoalTarget });
        sessionStorage.setItem('dailyGoalCelebrationShown', 'true');
        playCelebrationSound();
      }
      setLastDailyGoalMet(dailyGoalMet);

      // Check if monthly goal is met (Revenue based) - USE CURRENT MONTH'S REVENUE
      const monthlyGoalTarget = goalRes.data?.monthly_revenue_target || 1500000;
      const monthlyRevenue = statsRes.data?.monthly_revenue || 0;
      const monthlyGoalMet = monthlyGoalTarget > 0 && monthlyRevenue >= monthlyGoalTarget;
      
      if (monthlyGoalMet && !lastMonthlyGoalMet && sessionStorage.getItem('monthlyGoalCelebrationShown') !== 'true') {
        setGoalCelebration({ type: 'monthly', revenue: monthlyRevenue, target: monthlyGoalTarget });
        sessionStorage.setItem('monthlyGoalCelebrationShown', 'true');
        playCelebrationSound();
      }
      setLastMonthlyGoalMet(monthlyGoalMet);

      // Calculate collected total from revenue leaderboard
      // This is a workaround - ideally backend would return this
      const totalCollected = revenueLeaderRes.data.reduce((sum, a) => sum + (a.total_revenue * 0.7), 0); // Estimate
      setCollectedTotal(totalCollected);

      if (celebrationsRes.data.length > 0) {
        const latestCelebration = celebrationsRes.data[0];
        const storedId = lastCelebrationIdRef.current;
        
        // Yeni satış kontrolü:
        // 1. storedId varsa ve farklıysa = yeni satış
        // 2. storedId yoksa AMA celebration son 60 saniye içindeyse = yeni satış
        const celebrationTime = new Date(latestCelebration.timestamp).getTime();
        const now = Date.now();
        const isRecent = (now - celebrationTime) < 60000; // 60 saniye içinde
        
        const isNewCelebration = (storedId && latestCelebration.id !== storedId) || (!storedId && isRecent);
        
        if (isNewCelebration && !countdownCelebration) {
          // Yeni satış var - countdown başlat
          console.log("YENİ SATIŞ ALGILANDI!", latestCelebration.agent_name);
          setCountdownCelebration(latestCelebration);
        }
        
        // Son celebration ID'yi kaydet
        if (latestCelebration.id !== storedId) {
          localStorage.setItem('lastCelebrationId', latestCelebration.id);
          lastCelebrationIdRef.current = latestCelebration.id;
          setLastCelebrationId(latestCelebration.id);
        }
      }

      setLoading(false);
    } catch (error) { console.error("Error fetching data:", error); setLoading(false); }
  }, [dateRange, playCelebrationSound, countdownCelebration]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(), 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleDateChange = (range) => {
    setDateRange(range);
    fetchData(range);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
          <Zap className="w-16 h-16 text-emerald-400" />
        </motion.div>
      </div>
    );
  }

  const salesLeader = leaderboard[0];
  const revenueLeader = revenueLeaderboard[0];

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden" data-testid="dashboard">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
      </div>

      {/* Celebration Modal */}
      <AnimatePresence>
        {activeCelebration && <CelebrationModal celebration={activeCelebration} onClose={() => setActiveCelebration(null)} />}
      </AnimatePresence>

      {/* Countdown Celebration - New Sale */}
      <AnimatePresence>
        {countdownCelebration && (
          <CountdownCelebration 
            sale={countdownCelebration} 
            onComplete={() => setCountdownCelebration(null)} 
          />
        )}
      </AnimatePresence>

      {/* Goal Achievement Celebration Modal */}
      <AnimatePresence>
        {goalCelebration && <GoalCelebrationModal goalData={goalCelebration} onClose={() => setGoalCelebration(null)} />}
      </AnimatePresence>

      {/* Header */}
      <header className="relative z-10 px-4 md:px-6 py-3 md:py-4 flex flex-col md:flex-row md:items-center md:justify-between border-b border-zinc-800/50 gap-3">
        <div>
          <h1 className="font-heading text-xl md:text-3xl font-bold bg-gradient-to-r from-white via-emerald-200 to-emerald-400 bg-clip-text text-transparent">
            Satış Performans Merkezi
          </h1>
          <p className="text-zinc-500 text-xs md:text-sm">{currentTime.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        </div>
        
        <div className="flex items-center gap-2 md:gap-4 flex-wrap">
          <DateFilter dateRange={dateRange} setDateRange={setDateRange} onApply={handleDateChange} />
          <div className="font-mono text-lg md:text-2xl text-zinc-300">{currentTime.toLocaleTimeString('tr-TR')}</div>
          <Button variant="outline" size="icon" onClick={() => setIsMuted(!isMuted)} className="border-zinc-700 h-8 w-8 md:h-10 md:w-10" data-testid="mute-btn">
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </Button>
          <div className="flex items-center gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-full bg-red-500/10 border border-red-500/30">
            <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1, repeat: Infinity }} className="w-2 h-2 bg-red-500 rounded-full" />
            <span className="text-red-400 font-medium text-xs md:text-sm">CANLI</span>
          </div>
          <Link to="/admin">
            <Button variant="outline" className="gap-1 md:gap-2 border-zinc-700 text-xs md:text-sm h-8 md:h-10 px-2 md:px-4" data-testid="admin-btn">
              <Settings size={14} /> <span className="hidden sm:inline">Admin</span>
            </Button>
          </Link>
          {onLogout && (
            <Button variant="outline" onClick={onLogout} className="gap-1 md:gap-2 border-red-700 text-red-400 hover:bg-red-500/10 text-xs md:text-sm h-8 md:h-10 px-2 md:px-4" data-testid="logout-btn">
              <LogOut size={14} /> <span className="hidden sm:inline">Çıkış</span>
            </Button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 p-2 md:p-4">
        {/* Target Progress Bars + Call Stats - Responsive Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-2 md:mb-3">
          {/* Daily Target - Revenue Based */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }}
            className="p-2.5 rounded-xl bg-gradient-to-r from-lime-500/15 via-emerald-500/10 to-zinc-900/50 border border-lime-400/30 shadow-[0_0_20px_rgba(132,204,22,0.15)]"
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <Target size={14} className="text-lime-400" />
                <span className="font-medium text-lime-100 text-sm">Günlük Hedef</span>
              </div>
              <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-lime-300 to-emerald-300">
                %{goal?.revenue_target ? Math.min(100, Math.round(((stats?.total_revenue_today || 0) / goal.revenue_target) * 100)) : 0}
              </span>
            </div>
            <div className="relative h-2 bg-zinc-800/80 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${goal?.revenue_target ? Math.min(100, ((stats?.total_revenue_today || 0) / goal.revenue_target) * 100) : 0}%` }}
                transition={{ duration: 1 }}
                className="absolute h-full bg-gradient-to-r from-lime-500 via-emerald-400 to-teal-400 rounded-full shadow-[0_0_10px_rgba(132,204,22,0.5)]"
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-lime-300/60">
              <span>₺{(stats?.total_revenue_today || 0).toLocaleString()} / ₺{(goal?.revenue_target || 0).toLocaleString()}</span>
            </div>
          </motion.div>

          {/* Monthly Target - Revenue Based */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-2.5 rounded-xl bg-gradient-to-r from-yellow-500/15 via-amber-500/10 to-zinc-900/50 border border-yellow-400/30 shadow-[0_0_20px_rgba(250,204,21,0.15)]"
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <Trophy size={14} className="text-yellow-400" />
                <span className="font-medium text-yellow-100 text-sm">Aylık Hedef</span>
              </div>
              <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">
                %{goal ? Math.min(100, Math.round(((stats?.monthly_revenue || 0) / (goal.monthly_revenue_target || 1500000)) * 100)) : 0}
              </span>
            </div>
            <div className="relative h-2 bg-zinc-800/80 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${goal ? Math.min(100, ((stats?.monthly_revenue || 0) / (goal.monthly_revenue_target || 1500000)) * 100) : 0}%` }}
                transition={{ duration: 1, delay: 0.2 }}
                className="absolute h-full bg-gradient-to-r from-yellow-500 via-amber-400 to-orange-400 rounded-full shadow-[0_0_10px_rgba(250,204,21,0.5)]"
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-yellow-300/60">
              <span>Bu Ay: ₺{(stats?.monthly_revenue || 0).toLocaleString()} / ₺{(goal?.monthly_revenue_target || 1500000).toLocaleString()}</span>
            </div>
          </motion.div>

          {/* Günün Yıldızı */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-2.5 rounded-xl bg-gradient-to-r from-amber-500/20 via-yellow-500/15 to-zinc-900/50 border border-yellow-400/40 shadow-[0_0_25px_rgba(251,191,36,0.2)] relative overflow-hidden"
          >
            <div className="absolute -right-4 -top-4 w-16 h-16 rounded-full bg-yellow-500/10 blur-xl animate-pulse" />
            <div className="flex items-center gap-2 mb-1.5">
              <motion.div animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                <Star size={14} className="text-yellow-400 fill-yellow-400" />
              </motion.div>
              <span className="font-medium text-yellow-100 text-sm">Günün Yıldızı</span>
            </div>
            {starOfDay?.overall_star ? (
              <div className="text-center relative z-10">
                <div className="font-bold text-white text-sm truncate">{starOfDay.overall_star.name?.toUpperCase()}</div>
                <div className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-amber-300">
                  {starOfDay.revenue_star ? `₺${starOfDay.revenue_star.total_revenue?.toLocaleString()}` : `${starOfDay.calls_star?.total_calls} arama`}
                </div>
                {starOfDay.revenue_star && <span className="text-[10px] text-yellow-400/60">{starOfDay.revenue_star.total_sales} satış</span>}
              </div>
            ) : (
              <div className="text-center text-zinc-500 text-sm">Henüz yıldız yok</div>
            )}
          </motion.div>

          {/* Arama Hedefi Özeti */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-2.5 rounded-xl bg-gradient-to-r from-violet-500/15 via-purple-500/10 to-zinc-900/50 border border-violet-400/30 shadow-[0_0_20px_rgba(139,92,246,0.15)]"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <Target size={14} className="text-violet-400" />
              <span className="font-medium text-violet-100 text-sm">Arama Hedefi</span>
            </div>
            {callTargets.length > 0 ? (
              <div className="text-center">
                <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-violet-300 to-purple-300">
                  {callTargets.filter(a => a.progress >= 100).length}/{callTargets.length}
                </div>
                <span className="text-[10px] text-violet-400/60">Hedefe ulaşan ({dailyCallTarget} arama/gün)</span>
              </div>
            ) : (
              <div className="text-center text-zinc-500 text-sm">Veri yok</div>
            )}
          </motion.div>
        </div>

        {/* Metrics Row - Aylık Ciro | Dönem Ciro | Kayan Sloganlar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3 mb-2 md:mb-3">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.02 }}
            className="relative overflow-hidden rounded-2xl p-3 bg-gradient-to-br from-emerald-500/20 via-teal-500/10 to-cyan-900/20 border border-emerald-400/30 shadow-[0_0_40px_rgba(16,185,129,0.25)]"
          >
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-emerald-400/20 blur-2xl animate-pulse" />
            <div className="absolute -left-4 -bottom-4 w-24 h-24 rounded-full bg-cyan-400/15 blur-xl" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1 rounded-lg bg-emerald-400/20">
                  <DollarSign size={16} className="text-emerald-300" />
                </div>
                <span className="text-emerald-200/70 text-sm font-medium">
                  {dateRange.from ? "Dönem Cirosu" : "Toplam Ciro"}
                </span>
              </div>
              <div className="font-heading font-bold text-3xl text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300">
                <AnimatedCounter value={stats?.total_revenue || 0} prefix="₺" />
              </div>
              <p className="text-xs text-emerald-300/50">Toplam {stats?.total_sales || 0} satış</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.02 }}
            transition={{ delay: 0.1 }}
            className="relative overflow-hidden rounded-2xl p-3 bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-yellow-900/20 border border-amber-400/30 shadow-[0_0_40px_rgba(251,191,36,0.25)]"
          >
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-amber-400/20 blur-2xl animate-pulse" />
            <div className="absolute -left-4 -bottom-4 w-24 h-24 rounded-full bg-orange-400/15 blur-xl" />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1 rounded-lg bg-amber-400/20">
                  <TrendingUp size={16} className="text-amber-300" />
                </div>
                <span className="text-amber-200/70 text-sm font-medium">Bugünkü Ciro</span>
              </div>
              <div className="font-heading font-bold text-3xl text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-300 to-orange-300">
                <AnimatedCounter value={stats?.total_revenue_today || 0} prefix="₺" />
              </div>
              <p className="text-xs text-amber-300/50">Bugün {stats?.total_sales_today || 0} satış</p>
            </div>
          </motion.div>

          <MotivationalSlogans slogans={slogans} />
        </div>

        {/* Team Summary Stats */}
        <div className="grid grid-cols-3 md:grid-cols-7 gap-2 md:gap-3 mb-2 md:mb-3" data-testid="team-stats-bar">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="p-2 rounded-xl bg-zinc-900/60 border border-zinc-800/50 text-center">
            <p className="text-[10px] md:text-xs text-zinc-500 uppercase tracking-wider">Dönem Satış</p>
            <p className="text-lg md:text-2xl font-bold text-emerald-400">{(stats?.total_sales || 0).toLocaleString()}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="p-2 rounded-xl bg-zinc-900/60 border border-zinc-800/50 text-center">
            <p className="text-[10px] md:text-xs text-zinc-500 uppercase tracking-wider">Dönem Arama</p>
            <p className="text-lg md:text-2xl font-bold text-blue-400">{(stats?.total_calls || 0).toLocaleString()}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="p-2 rounded-xl bg-zinc-900/60 border border-zinc-800/50 text-center">
            <p className="text-[10px] md:text-xs text-zinc-500 uppercase tracking-wider">Bugün Satış</p>
            <p className="text-lg md:text-2xl font-bold text-amber-400">{(stats?.total_sales_today || 0).toLocaleString()}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="p-2 rounded-xl bg-zinc-900/60 border border-zinc-800/50 text-center">
            <p className="text-[10px] md:text-xs text-zinc-500 uppercase tracking-wider">Bugün Arama</p>
            <p className="text-lg md:text-2xl font-bold text-cyan-400">{(stats?.total_calls_today || 0).toLocaleString()}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="p-2 rounded-xl bg-zinc-900/60 border border-zinc-800/50 text-center">
            <p className="text-[10px] md:text-xs text-zinc-500 uppercase tracking-wider">Aylık Satış</p>
            <p className="text-lg md:text-2xl font-bold text-purple-400">{(stats?.monthly_sales || 0).toLocaleString()}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="p-2 rounded-xl bg-zinc-900/60 border border-zinc-800/50 text-center">
            <p className="text-[10px] md:text-xs text-zinc-500 uppercase tracking-wider">Aylık Arama</p>
            <p className="text-lg md:text-2xl font-bold text-rose-400">{(stats?.monthly_calls || 0).toLocaleString()}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className={`p-2 rounded-xl border text-center ${newDataCount > 0 ? 'bg-emerald-900/30 border-emerald-700/50' : 'bg-zinc-900/60 border-zinc-800/50'}`}>
            <p className="text-[10px] md:text-xs text-zinc-500 uppercase tracking-wider">Yeni Data</p>
            <p className={`text-lg md:text-2xl font-bold ${newDataCount > 0 ? 'text-emerald-400 animate-pulse' : 'text-zinc-500'}`}>{newDataCount}</p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8 gap-3 md:gap-4">
          {/* Ciro Sıralaması */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl p-3 bg-gradient-to-br from-zinc-900/60 to-zinc-950/60 border border-amber-900/30 h-full">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign size={18} className="text-amber-400" />
                <h2 className="font-heading text-sm md:text-base font-bold text-amber-400">Ciro Sıralaması</h2>
              </div>
              <div className="space-y-1.5" data-testid="leaderboard-revenue">
                {revenueLeaderboard.slice(0, 6).map((entry, i) => <LeaderboardItem key={entry.agent_id} entry={entry} index={i} type="revenue" />)}
                {revenueLeaderboard.length === 0 && <div className="text-center py-8 text-zinc-600">Henüz veri yok</div>}
              </div>
            </div>
          </div>

          {/* Bugünkü Satışlar */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl p-3 bg-gradient-to-br from-zinc-900/60 to-zinc-950/60 border border-emerald-900/30 h-full">
              <div className="flex items-center gap-2 mb-3">
                <Banknote size={18} className="text-emerald-400" />
                <h2 className="font-heading text-sm md:text-base font-bold text-emerald-400">Bugünkü Satışlar</h2>
              </div>
              <div className="space-y-1.5" data-testid="leaderboard-daily-sales">
                {dailySalesLeaderboard.slice(0, 6).map((entry, i) => <LeaderboardItem key={entry.agent_id} entry={entry} index={i} type="daily_revenue" />)}
                {dailySalesLeaderboard.length === 0 && <div className="text-center py-8 text-zinc-600">Bugün satış yok</div>}
              </div>
            </div>
          </div>

          {/* Günlük Arama Sayısı - Bugünün aramaları */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl p-3 bg-gradient-to-br from-zinc-900/60 to-zinc-950/60 border border-blue-900/30 h-full">
              <div className="flex items-center gap-2 mb-3">
                <Phone size={18} className="text-blue-400" />
                <h2 className="font-heading text-sm md:text-base font-bold text-blue-400">Günlük Arama Sayısı</h2>
              </div>
              <div className="space-y-1.5" data-testid="daily-calls-leaderboard">
                {dailyCallsLeaderboard.length > 0 ? (
                  dailyCallsLeaderboard.slice(0, 6).map((entry, i) => (
                    <motion.div
                      key={entry.agent_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className={`flex items-center gap-3 p-2.5 rounded-xl ${
                        i === 0 ? "bg-gradient-to-r from-blue-500/20 to-transparent" :
                        i === 1 ? "bg-gradient-to-r from-slate-400/10 to-transparent" :
                        i === 2 ? "bg-gradient-to-r from-cyan-500/10 to-transparent" : "bg-zinc-900/30"
                      } hover:bg-white/5 transition-all`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                        i === 0 ? "bg-gradient-to-br from-blue-400 to-cyan-600 text-white shadow-[0_0_15px_rgba(59,130,246,0.5)]" :
                        i === 1 ? "bg-gradient-to-br from-slate-300 to-slate-500 text-black" :
                        i === 2 ? "bg-gradient-to-br from-cyan-400 to-blue-600 text-white" : "bg-zinc-700 text-zinc-300"
                      }`}>
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white text-base truncate">{entry.name.toUpperCase()}</h3>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-bold text-xl text-blue-400">{entry.total_calls.toLocaleString()}</div>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-8 text-zinc-600">Henüz arama yok</div>
                )}
              </div>
            </div>
          </div>

          {/* Arama Hedefi İlerleme */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl p-3 bg-gradient-to-br from-zinc-900/60 to-zinc-950/60 border border-violet-900/30 h-full">
              <div className="flex items-center gap-2 mb-3">
                <Target size={18} className="text-violet-400" />
                <h2 className="font-heading text-sm md:text-base font-bold text-violet-400">Arama Hedefi ({dailyCallTarget})</h2>
              </div>
              <div className="space-y-1.5" data-testid="call-targets">
                {callTargets.length > 0 ? (
                  callTargets.slice(0, 8).map((agent, i) => (
                    <motion.div
                      key={agent.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className={`flex items-center gap-2 p-1.5 rounded-lg ${agent.progress >= 100 ? 'bg-emerald-500/10' : 'bg-zinc-900/30'}`}
                    >
                      <div className="w-5 h-5 rounded flex items-center justify-center text-xs font-bold" style={{ backgroundColor: agent.avatar_color || '#6b7280', color: '#fff' }}>
                        {agent.name?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs text-white truncate font-medium">{agent.name?.split(' ')[0]}</span>
                          <span className={`text-xs font-bold ${agent.progress >= 100 ? 'text-emerald-400' : agent.progress >= 60 ? 'text-amber-400' : 'text-zinc-400'}`}>
                            {agent.total_calls}/{agent.target}
                          </span>
                        </div>
                        <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.min(agent.progress, 100)}%` }}
                            transition={{ duration: 0.8, delay: i * 0.1 }}
                            className={`h-full rounded-full ${
                              agent.progress >= 100 ? 'bg-gradient-to-r from-emerald-500 to-green-400' :
                              agent.progress >= 60 ? 'bg-gradient-to-r from-amber-500 to-yellow-400' :
                              'bg-gradient-to-r from-violet-500 to-purple-400'
                            }`}
                          />
                        </div>
                      </div>
                      {agent.progress >= 100 && (
                        <motion.div animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1, repeat: Infinity }}>
                          <Star size={12} className="text-yellow-400 fill-yellow-400" />
                        </motion.div>
                      )}
                    </motion.div>
                  ))
                ) : (
                  <div className="text-center py-8 text-zinc-600">Veri yok</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-zinc-900/90 backdrop-blur-sm border-t border-zinc-800/50 px-3 md:px-6 py-2 md:py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 md:gap-6 text-xs md:text-sm">
            <div className="flex items-center gap-1 md:gap-2">
              <Users size={14} className="text-zinc-500" />
              <span className="text-zinc-400 hidden sm:inline">Ekip:</span>
              <span className="font-bold text-white">{stats?.total_agents || 0}</span>
            </div>
            <div className="flex items-center gap-1 md:gap-2">
              <TrendingUp size={14} className="text-emerald-500" />
              <span className="text-zinc-400 hidden sm:inline">Başarı:</span>
              <span className="font-bold text-emerald-400">{stats?.avg_success_rate || 0}%</span>
            </div>
            {dateRange.from && (
              <div className="flex items-center gap-1 md:gap-2">
                <Calendar size={14} className="text-purple-500" />
                <span className="text-purple-400 font-medium text-xs">
                  {dateRange.from && dateRange.to ? 
                    `${format(dateRange.from, 'd MMM', { locale: tr })} - ${format(dateRange.to, 'd MMM', { locale: tr })}` : 
                    'Tüm Zamanlar'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
